import {
  HHButton,
  HHError,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { DataGridPro, GridPaginationModel } from '@mui/x-data-grid-pro';
import {
  Dispatch,
  forwardRef,
  SetStateAction,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useDynamicDialogContext } from '../../context/dynamic-dialog.context';
import { useDynamicDrawerContext } from '../../context/dynamic-drawer.context';
import { useSnackbarContext } from '../../context/snackbar.context';
import { useBillingWriteAccess } from '../../hooks/use-billing-write-access';
import {
  ClientType,
  FeeScheduleEntityType,
  PartnershipType,
  PaymentSystemType,
  PaymentType,
  useDeleteFeeScheduleMutation,
  useUpdateFeeScheduleMutation,
} from '../../types';
import { NoRowsOverlay } from '../grid-components/no-rows-or-error-overlay';
import {
  FeeScheduleFilterBar,
  FeeScheduleFilterParams,
} from './fee-schedule-filter-bar';
import { EntityOption, FeeScheduleForm } from './fee-schedule-form/form';
import { FeeScheduleFormValues } from './fee-schedule-form/validation-schema';
import { useFeeScheduleColumns } from './use-fee-schedule-columns';
import {
  FeeScheduleRow,
  useFeeScheduleQueries,
} from './use-fee-schedule-queries';

export interface FeeScheduleGridHandle {
  reload: () => void;
}

export interface FeeScheduleGridProps {
  entityType: FeeScheduleEntityType;
  filterParams: FeeScheduleFilterParams;
  setFilterParams: Dispatch<SetStateAction<FeeScheduleFilterParams>>;
  clients: ClientType[];
  clientsLoading: boolean;
  partnerships: PartnershipType[];
  partnershipsLoading: boolean;
  paymentTypes: PaymentType[];
  paymentTypesLoading: boolean;
}

export const FEE_SCHEDULE_GRID_TEST_ID = 'fee-schedule-grid';
export const FEE_SCHEDULE_DELETE_CANCEL_TEST_ID =
  'fee-schedule-delete-cancel-button';
export const FEE_SCHEDULE_DELETE_CONFIRM_TEST_ID =
  'fee-schedule-delete-confirm-button';

const DEFAULT_PAGE_SIZE = 25;

export const FeeScheduleGrid = forwardRef<
  FeeScheduleGridHandle,
  FeeScheduleGridProps
>(
  (
    {
      entityType,
      filterParams,
      setFilterParams,
      clients,
      clientsLoading,
      partnerships,
      partnershipsLoading,
      paymentTypes,
      paymentTypesLoading,
    },
    ref,
  ): JSX.Element => {
    const canWrite = useBillingWriteAccess();
    const { setDrawerContent, onClose } = useDynamicDrawerContext();
    const { setDialogContent, closeDialog } = useDynamicDialogContext();
    const { setSnackbarState } = useSnackbarContext();

    const [updateFeeSchedule] = useUpdateFeeScheduleMutation();
    const [deleteFeeSchedule] = useDeleteFeeScheduleMutation();

    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
      {
        page: 0,
        pageSize: DEFAULT_PAGE_SIZE,
      },
    );

    const handleFiltersApplied = useCallback((): void => {
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    }, []);

    const { rows, loading, hasError, refetch } = useFeeScheduleQueries(
      entityType,
      filterParams,
    );

    useImperativeHandle(ref, () => ({ reload: refetch }), [refetch]);

    const entities: EntityOption[] = useMemo(() => {
      if (entityType === FeeScheduleEntityType.Client) {
        return clients.map(c => ({ id: c.id, name: c.name }));
      }
      return partnerships.map(p => ({ id: p.id, name: p.name }));
    }, [entityType, clients, partnerships]);

    const entitiesLoading =
      entityType === FeeScheduleEntityType.Client
        ? clientsLoading
        : partnershipsLoading;

    const handleEdit = useCallback(
      (row: FeeScheduleRow): void => {
        const initialValues: FeeScheduleFormValues = {
          entityId: row.entityId,
          paymentType: row.paymentType,
          paymentSystem: row.paymentSystem,
          price: row.price,
          enabled: row.enabled,
          notes: row.notes ?? '',
        };

        const handleUpdate = async (
          values: FeeScheduleFormValues,
        ): Promise<void> => {
          try {
            await updateFeeSchedule({
              variables: {
                id: row.id,
                feeScheduleInput: {
                  paymentType: values.paymentType,
                  paymentSystem: values.paymentSystem as PaymentSystemType,
                  price: values.price as number,
                  enabled: values.enabled,
                  notes: values.notes || null,
                },
              },
            });
            setSnackbarState(
              'Fee schedule updated successfully',
              'success',
              3000,
            );
            onClose();
            refetch();
          } catch {
            setSnackbarState('Error updating fee schedule', 'error', 3000);
          }
        };

        setDrawerContent(
          <HHStack padding={4}>
            <FeeScheduleForm
              onSubmit={handleUpdate}
              initialValues={initialValues}
              entityType={entityType}
              entities={entities}
              entitiesLoading={entitiesLoading}
              paymentTypes={paymentTypes}
              paymentTypesLoading={paymentTypesLoading}
            />
          </HHStack>,
          <HHTypography hhVariant="h6">Edit Fee</HHTypography>,
          undefined,
          'temporary',
        );
      },
      [
        setDrawerContent,
        updateFeeSchedule,
        entityType,
        entities,
        entitiesLoading,
        paymentTypes,
        paymentTypesLoading,
        setSnackbarState,
        onClose,
        refetch,
      ],
    );

    const handleDelete = useCallback(
      (row: FeeScheduleRow): void => {
        const executeDelete = async (): Promise<void> => {
          closeDialog();
          try {
            await deleteFeeSchedule({
              variables: { id: row.id },
            });
            setSnackbarState(
              'Fee schedule deleted successfully',
              'success',
              3000,
            );
            refetch();
          } catch {
            setSnackbarState('Error deleting fee schedule', 'error', 3000);
          }
        };

        setDialogContent({
          title: (
            <HHTypography hhVariant="section-title">
              Delete Fee Schedule
            </HHTypography>
          ),
          content: (
            <HHStack spacing={2}>
              <HHTypography hhVariant="body1">
                Are you sure you want to delete this fee schedule (
                {row.entityName} — {row.paymentType})?
              </HHTypography>
            </HHStack>
          ),
          footer: (
            <HHStack direction="row" spacing={2} padding={1}>
              <HHButton
                hhVariant="variant-bypass"
                variant="outlined"
                onClick={closeDialog}
                data-testid={FEE_SCHEDULE_DELETE_CANCEL_TEST_ID}
              >
                Cancel
              </HHButton>
              <HHButton
                hhVariant="variant-bypass"
                variant="contained"
                color="error"
                onClick={(): void => {
                  void executeDelete();
                }}
                data-testid={FEE_SCHEDULE_DELETE_CONFIRM_TEST_ID}
              >
                Delete
              </HHButton>
            </HHStack>
          ),
          width: 'sm',
          fullWidth: true,
        });
      },
      [
        setDialogContent,
        closeDialog,
        deleteFeeSchedule,
        setSnackbarState,
        refetch,
      ],
    );

    const columns = useFeeScheduleColumns({
      entityType,
      onEdit: handleEdit,
      onDelete: handleDelete,
      canWrite,
    });

    if (hasError) {
      return (
        <HHError
          body="Error fetching fee schedules"
          hhVariant="variant-bypass"
        />
      );
    }

    return (
      <HHStack
        flex={1}
        direction="column"
        spacing={6}
        justifyContent="space-between"
        minHeight="0"
      >
        <HHStack paddingLeft={6}>
          <FeeScheduleFilterBar
            filterParams={filterParams}
            setFilterParams={setFilterParams}
            entityType={entityType}
            clients={clients}
            clientsLoading={clientsLoading}
            partnerships={partnerships}
            partnershipsLoading={partnershipsLoading}
            onFiltersApplied={handleFiltersApplied}
          />
        </HHStack>

        <DataGridPro
          columns={columns}
          rows={rows}
          loading={loading}
          getRowId={(row): number => row.id}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          paginationMode="client"
          initialState={{
            sorting: { sortModel: [{ field: 'entityName', sort: 'asc' }] },
          }}
          sortingMode="client"
          filterMode="client"
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          pinnedColumns={{ right: ['actions'] }}
          aria-label="Fee schedules table"
          data-testid={FEE_SCHEDULE_GRID_TEST_ID}
          {...(process.env.NODE_ENV === 'test' && {
            disableVirtualization: true,
          })}
          slots={{
            noResultsOverlay: NoRowsOverlay,
            noRowsOverlay: NoRowsOverlay,
          }}
          slotProps={{
            noResultsOverlay: { error: hasError } as Record<string, boolean>,
            noRowsOverlay: { error: hasError } as Record<string, boolean>,
          }}
        />
      </HHStack>
    );
  },
);

FeeScheduleGrid.displayName = 'FeeScheduleGrid';
